import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Stack,
  CircularProgress,
  Fade,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { UserUpdate } from '../../../types/user';

interface ProfileEditFormProps {
  initialValues: {
    name: string;
    email: string;
  };
  onSubmit: (data: UserUpdate) => void;
  isLoading: boolean;
  error?: string | null;
  success?: boolean;
}

export const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  initialValues,
  onSubmit,
  isLoading,
  error,
  success,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<UserUpdate & { email: string }>({
    defaultValues: initialValues,
  });

  // Update form values if initialValues change
  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 4,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        height: '100%',
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        프로필 수정
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="이메일"
                fullWidth
                disabled
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  sx: { bgcolor: 'action.hover' },
                }}
                helperText="이메일은 변경할 수 없습니다."
              />
            )}
          />

          <Controller
            name="name"
            control={control}
            rules={{
              required: '이름을 입력해주세요.',
              minLength: {
                value: 2,
                message: '이름은 2글자 이상이어야 합니다.',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="이름"
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={isLoading}
              />
            )}
          />

          <Box sx={{ minHeight: 48 }}>
            {error && (
              <Fade in>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              </Fade>
            )}
            {success && (
              <Fade in>
                <Alert severity="success" sx={{ mb: 2 }}>
                  프로필이 성공적으로 수정되었습니다.
                </Alert>
              </Fade>
            )}
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading || !isDirty}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontWeight: 600,
              boxShadow: 2,
            }}
          >
            {isLoading ? '저장 중...' : '저장'}
          </Button>
        </Stack>
      </form>
    </Paper>
  );
};
